import { MediaUploadError } from '@/hooks/use-media-upload.ts';
import { ServerError } from '@/types/errors/server.error.ts';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto.ts';
import type { SessionDto } from '@/types/dtos/sessions/session.dto.ts';
import { FLOW_PARENT_ENTITY } from '@/types/dtos/flows/response/flow-parent-entity.enum';
import { SESSION_PARENT_ENTITY } from '@/types/dtos/sessions/session-parent-entity.enum';
import { MEDIA_PARENT_ENTITY, type MediaParentEntity } from '@/types/dtos/media/media-parent-entity.enum.ts';
import { resolveRefId } from '@/helpers/object.id.helper.ts';

export type MediaUploadQuotaModalChoice = 'purchase' | 'contact';

/** Event id for create_order / quota modals when media parent is (or rolls up to) an event. */
export function getEventIdForMediaUploadQuota(
  entity: SessionDto | FlowDto,
  entityType: MediaParentEntity,
): string | undefined {
  if (entityType === MEDIA_PARENT_ENTITY.FLOW) {
    const flow = entity as FlowDto;
    if (flow.parentType !== FLOW_PARENT_ENTITY.EVENT) {
      return undefined;
    }
    return resolveRefId(flow.parent as Parameters<typeof resolveRefId>[0]);
  }
  if (entityType === MEDIA_PARENT_ENTITY.SESSION) {
    const session = entity as SessionDto;
    if (session.parentType !== SESSION_PARENT_ENTITY.EVENT) {
      return undefined;
    }
    return resolveRefId(session.parent as Parameters<typeof resolveRefId>[0]);
  }
  return undefined;
}

export function getMediaUploadErrorCode(error: unknown): string | undefined {
  if (error instanceof MediaUploadError) {
    return error.errorCode;
  }
  if (error instanceof ServerError) {
    return error.errorCode;
  }
  if (typeof error === 'object' && error !== null && 'errorCode' in error) {
    const code = (error as { errorCode?: unknown }).errorCode;
    return typeof code === 'string' ? code : undefined;
  }
  return undefined;
}

export function getInsufficientStorageQuotaModalChoice(
  error: unknown,
  canPurchaseFromPermissions: boolean,
  canPurchaseFromPreflight?: boolean,
): MediaUploadQuotaModalChoice | null {
  const errorCode = getMediaUploadErrorCode(error);

  if (errorCode !== 'INSUFFICIENT_STORAGE_SPACE') {
    return null;
  }

  const canPurchaseFromError =
    error instanceof MediaUploadError ? error.quotaBlocked?.preflight.canPurchaseAdditionalStorage : undefined;

  const canPurchase = canPurchaseFromPreflight ?? canPurchaseFromError ?? canPurchaseFromPermissions;

  return canPurchase ? 'purchase' : 'contact';
}
